// src/stores/WorkflowStore.js - Zustand Store for Workflow Management
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useWorkflowStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Main workflows data
    workflows: [],
    workflowsLoading: false,

    // Selected workflow state
    selectedWorkflow: null,
    selectedWorkflowLoading: false,

    // View mode toggle
    mode: 'edit', // 'edit' or 'view'

    // Kanban board data (customers in workflow - for view mode)
    workflowBoard: null, // { stages: [{id, name, items: [{customer, ...}]}] }
    boardLoading: false,

    // Saving flags (granular per operation type)
    creatingWorkflow: false,
    savingWorkflow: false,        // metadata: toggle active, rename
    savingWorkflowConfig: false,  // canvas auto-save
    deletingWorkflow: false,

    // Client automation state (for client_automation workflow type)
    automationRuns: {}, // { [workflowId]: [runs] }
    automationRunsLoading: false,

    // Connection state
    isConnected: false,

    // Cache state
    cache: {
      workflows: [],
      selectedWorkflow: null,
      workflowBoard: null,
      lastFetch: null
    },

    // Internal flags
    _initialized: false,

    // ================================
    // MODE SWITCHING
    // ================================

    setMode: (mode) => set({ mode }),

    // ================================
    // WORKFLOW CRUD OPERATIONS
    // ================================

    /**
     * Request all workflows from server
     */
    loadWorkflows: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('🔄 WorkflowStore: Loading workflows');

      set({
        workflowsLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_WORKFLOWS, {});
    },

    /**
     * Request detailed information for a specific workflow
     */
    loadWorkflowDetails: (workflowId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('📋 WorkflowStore: Loading workflow details:', workflowId);

      set({
        selectedWorkflowLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_WORKFLOW, {
        workflow_id: workflowId
      });
    },

    /**
     * Request to create a new workflow
     */
    createWorkflow: (workflowData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('➕ WorkflowStore: Creating workflow:', workflowData.name);

      set({ creatingWorkflow: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_WORKFLOW, workflowData);
    },

    /**
     * Request to update an existing workflow
     */
    updateWorkflow: (workflowId, updateData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('✏️ WorkflowStore: Updating workflow:', workflowId);

      set({ savingWorkflow: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_WORKFLOW, {
        workflow_id: workflowId,
        ...updateData
      });
    },

    /**
     * Request to delete a workflow
     */
    deleteWorkflow: (workflowId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('🗑️ WorkflowStore: Deleting workflow:', workflowId);

      set({ deletingWorkflow: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_WORKFLOW, {
        workflow_id: workflowId
      });
    },

    /**
     * Update workflow's flow configuration (React Flow nodes/edges)
     */
    updateWorkflowConfig: (workflowId, flowConfig) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('💾 WorkflowStore: Updating workflow config:', workflowId);

      set({ savingWorkflowConfig: true });
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_WORKFLOW_CONFIG, {
        workflow_id: workflowId,
        flow_config: flowConfig
      });
    },

    /**
     * Save flow configuration (nodes and edges) - DEPRECATED: Use updateWorkflowConfig instead
     * @deprecated Use updateWorkflowConfig instead
     */
    saveFlowConfig: (workflowId, flowConfig) => {
      console.warn('⚠️ saveFlowConfig is deprecated, use updateWorkflowConfig instead');
      get().updateWorkflowConfig(workflowId, flowConfig);
    },

    // ================================
    // KANBAN/ITEM MANAGEMENT OPERATIONS
    // ================================

    /**
     * Request kanban board data for a workflow
     */
    loadWorkflowBoard: (workflowId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('📊 WorkflowStore: Loading kanban board for workflow:', workflowId);

      set({
        boardLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_WORKFLOW_BOARD, {
        workflow_id: workflowId
      });
    },

    /**
     * Add a customer to a workflow
     */
    addCustomerToWorkflow: (workflowId, customerId, stageId = null) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('➕ WorkflowStore: Adding customer to workflow:', customerId, workflowId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.ADD_CUSTOMER_TO_WORKFLOW, {
        workflow_id: workflowId,
        customer_id: customerId,
        stage_id: stageId
      });
    },

    /**
     * Move a customer to a different node
     */
    moveCustomerToNode: (itemId, targetNodeId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('🔀 WorkflowStore: Moving customer to node:', itemId, targetNodeId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.MOVE_CUSTOMER_TO_NODE, {
        item_id: itemId,
        target_node_id: targetNodeId
      });
    },

    /**
     * Remove a customer from a workflow
     */
    removeCustomerFromWorkflow: (itemId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('➖ WorkflowStore: Removing customer from workflow:', itemId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.REMOVE_CUSTOMER_FROM_WORKFLOW, {
        item_id: itemId
      });
    },

    // ================================
    // CLIENT AUTOMATION OPERATIONS
    // ================================

    /**
     * Load automation runs for a workflow
     */
    loadAutomationRuns: (workflowId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('📊 WorkflowStore: Loading automation runs for:', workflowId);

      set({ automationRunsLoading: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_AUTOMATION_RUNS, {
        workflow_id: workflowId,
        limit: 50,
        offset: 0
      });
    },

    /**
     * Load logs for a specific automation run
     * @returns Promise that resolves with logs array
     */
    loadAutomationLogs: (workflowId, runId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return Promise.resolve([]);
      }

      console.log('📋 WorkflowStore: Loading automation logs for run:', runId);

      return new Promise((resolve) => {
        // Set up one-time listener for the response
        const handler = (data) => {
          if (data.run_id === runId) {
            socketManager.universalHandler.off(SOCKET_EVENTS.AUTOMATION_LOGS, handler);
            resolve(data.logs || []);
          }
        };

        socketManager.universalHandler.on(SOCKET_EVENTS.AUTOMATION_LOGS, handler);

        // Also handle error case
        const errorHandler = () => {
          socketManager.universalHandler.off(SOCKET_EVENTS.GET_AUTOMATION_LOGS_FAILED, errorHandler);
          resolve([]);
        };
        socketManager.universalHandler.on(SOCKET_EVENTS.GET_AUTOMATION_LOGS_FAILED, errorHandler);

        // Send the request
        socketManager.universalHandler.emit(SOCKET_EVENTS.GET_AUTOMATION_LOGS, {
          run_id: runId
        });

        // Timeout after 10 seconds
        setTimeout(() => {
          socketManager.universalHandler.off(SOCKET_EVENTS.AUTOMATION_LOGS, handler);
          socketManager.universalHandler.off(SOCKET_EVENTS.GET_AUTOMATION_LOGS_FAILED, errorHandler);
          resolve([]);
        }, 10000);
      });
    },

    /**
     * Trigger a manual automation run
     */
    triggerAutomation: (workflowId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return Promise.resolve(false);
      }

      console.log('🚀 WorkflowStore: Triggering automation:', workflowId);

      return new Promise((resolve) => {
        const handler = (data) => {
          if (data.workflow_id === workflowId) {
            socketManager.universalHandler.off(SOCKET_EVENTS.AUTOMATION_RUN_STARTED, handler);
            resolve(true);
          }
        };

        socketManager.universalHandler.on(SOCKET_EVENTS.AUTOMATION_RUN_STARTED, handler);

        const errorHandler = () => {
          socketManager.universalHandler.off(SOCKET_EVENTS.TRIGGER_AUTOMATION_FAILED, errorHandler);
          resolve(false);
        };
        socketManager.universalHandler.on(SOCKET_EVENTS.TRIGGER_AUTOMATION_FAILED, errorHandler);

        socketManager.universalHandler.emit(SOCKET_EVENTS.TRIGGER_AUTOMATION, {
          workflow_id: workflowId
        });

        setTimeout(() => {
          socketManager.universalHandler.off(SOCKET_EVENTS.AUTOMATION_RUN_STARTED, handler);
          socketManager.universalHandler.off(SOCKET_EVENTS.TRIGGER_AUTOMATION_FAILED, errorHandler);
          resolve(false);
        }, 30000);
      });
    },

    /**
     * Update automation schedule
     */
    updateAutomationSchedule: (workflowId, schedule) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WorkflowStore: Socket service not initialized');
        return;
      }

      console.log('⏰ WorkflowStore: Updating automation schedule:', workflowId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_AUTOMATION_SCHEDULE, {
        workflow_id: workflowId,
        schedule
      });
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    clearSelectedWorkflow: () => {
      set({
        selectedWorkflow: null
      });
    },

    refreshWorkflows: () => {
      // Clear cache first
      set(state => ({
        cache: {
          ...state.cache,
          workflows: [],
          selectedWorkflow: null,
          lastFetch: null
        }
      }));

      // Then reload
      get().loadWorkflows();
    },

    getWorkflowById: (workflowId) => {
      const { workflows } = get();
      return workflows.find(w => w.id === workflowId) || null;
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setWorkflowsLoaded: (data) => {
      const workflows = Array.isArray(data) ? data : (data.data || []);

      console.log('📡 WorkflowStore: Workflows received:', workflows.length);

      set(state => ({
        workflows,
        workflowsLoading: false,
        cache: {
          ...state.cache,
          workflows,
          lastFetch: new Date()
        }
      }));
    },

    _setWorkflowDetailsLoaded: (workflowData) => {
      console.log('📡 WorkflowStore: Workflow details received:', workflowData.name);

      set(state => {
        const existingIndex = state.workflows.findIndex(w => w.id === workflowData.id);
        const updatedWorkflows = existingIndex !== -1
          ? state.workflows.map((w, index) =>
              index === existingIndex ? { ...w, ...workflowData } : w
            )
          : [workflowData, ...state.workflows];

        return {
          workflows: updatedWorkflows,
          selectedWorkflow: workflowData,
          selectedWorkflowLoading: false,
          cache: {
            ...state.cache,
            workflows: updatedWorkflows,
            selectedWorkflow: workflowData
          }
        };
      });
    },

    _addWorkflow: (workflowData) => {
      console.log('📡 WorkflowStore: New workflow created:', workflowData.name);

      set(state => {
        const exists = state.workflows.find(w => w.id === workflowData.id);
        if (exists) return state;

        const updatedWorkflows = [workflowData, ...state.workflows];

        return {
          workflows: updatedWorkflows,
          cache: {
            ...state.cache,
            workflows: updatedWorkflows
          }
        };
      });
    },

    _updateWorkflow: (workflowData) => {
      console.log('📡 WorkflowStore: Workflow updated:', workflowData.name || workflowData.id);

      set(state => {
        const updatedWorkflows = state.workflows.map(w =>
          w.id === workflowData.id ? { ...w, ...workflowData } : w
        );

        const updatedSelectedWorkflow = state.selectedWorkflow?.id === workflowData.id
          ? { ...state.selectedWorkflow, ...workflowData }
          : state.selectedWorkflow;

        return {
          workflows: updatedWorkflows,
          selectedWorkflow: updatedSelectedWorkflow,
          cache: {
            ...state.cache,
            workflows: updatedWorkflows,
            selectedWorkflow: updatedSelectedWorkflow
          }
        };
      });
    },

    _removeWorkflow: (workflowId) => {
      console.log('📡 WorkflowStore: Workflow deleted:', workflowId);

      set(state => {
        const updatedWorkflows = state.workflows.filter(w => w.id !== workflowId);

        const updatedSelectedWorkflow = state.selectedWorkflow?.id === workflowId
          ? null
          : state.selectedWorkflow;

        return {
          workflows: updatedWorkflows,
          selectedWorkflow: updatedSelectedWorkflow,
          cache: {
            ...state.cache,
            workflows: updatedWorkflows,
            selectedWorkflow: updatedSelectedWorkflow
          }
        };
      });
    },

    _setBoardLoaded: (boardData) => {
      console.log('📡 WorkflowStore: Board data received');

      set(state => ({
        workflowBoard: boardData,
        boardLoading: false,
        cache: {
          ...state.cache,
          workflowBoard: boardData
        }
      }));
    },

    // Automation state updaters
    _setAutomationRunsLoaded: (data) => {
      console.log('📡 WorkflowStore: Automation runs received for:', data.workflow_id);

      set(state => ({
        automationRuns: {
          ...state.automationRuns,
          [data.workflow_id]: data.runs || []
        },
        automationRunsLoading: false
      }));
    },

    _addAutomationRun: (data) => {
      console.log('📡 WorkflowStore: New automation run started:', data.workflow_id);

      set(state => {
        const existingRuns = state.automationRuns[data.workflow_id] || [];
        return {
          automationRuns: {
            ...state.automationRuns,
            [data.workflow_id]: [data.run, ...existingRuns]
          }
        };
      });
    },

    _updateAutomationRun: (data) => {
      console.log('📡 WorkflowStore: Automation run updated:', data.run?.id);

      set(state => {
        const workflowId = data.workflow_id;
        const existingRuns = state.automationRuns[workflowId] || [];
        const updatedRuns = existingRuns.map(run =>
          run.id === data.run?.id ? { ...run, ...data.run } : run
        );

        return {
          automationRuns: {
            ...state.automationRuns,
            [workflowId]: updatedRuns
          }
        };
      });
    },

    // ================================
    // INTERNAL METHODS (socket manager, initialization)
    // ================================

    _getSocketManager: () => {
      return {
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null
      };
    },

    _initialize: (socketManager) => {
      const state = get();

      console.log('🔌 WorkflowStore: Initializing with socket manager');

      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false
      });

      // Auto-load workflows if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('🔄 WorkflowStore: Auto-loading workflows (first time)');
        state.loadWorkflows();
      }
    },

    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },

    _handleDisconnect: () => {
      set({
        creatingWorkflow: false,
        savingWorkflow: false,
        savingWorkflowConfig: false,
        deletingWorkflow: false,
        workflowsLoading: false,
        selectedWorkflowLoading: false,
        boardLoading: false,
        automationRunsLoading: false,
      });
    },

    _handleAuthReset: () => {
      console.log('🧹 WorkflowStore: Resetting all state due to auth reset');

      set({
        workflows: [],
        workflowsLoading: false,
        selectedWorkflow: null,
        selectedWorkflowLoading: false,
        creatingWorkflow: false,
        savingWorkflow: false,
        savingWorkflowConfig: false,
        deletingWorkflow: false,
        workflowBoard: null,
        boardLoading: false,
        automationRuns: {},
        automationRunsLoading: false,
        mode: 'edit',
        isConnected: false,
        cache: {
          workflows: [],
          selectedWorkflow: null,
          workflowBoard: null,
          lastFetch: null
        },
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP (Global initialization)
// ================================

export const initializeWorkflowStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }

  console.log('👂 WorkflowStore: Setting up socket event listeners');

  const { universalHandler } = socketManager;

  // Set up universal response listener for all workflow events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useWorkflowStore.getState();

    switch (eventName) {
      // ✅ WORKFLOW SUCCESS EVENTS
      case SOCKET_EVENTS.WORKFLOWS_LIST:
        store._setWorkflowsLoaded(responseData.data || responseData);
        break;

      case SOCKET_EVENTS.WORKFLOW_DETAILS:
        store._setWorkflowDetailsLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.WORKFLOW_CREATED:
        useWorkflowStore.setState({ creatingWorkflow: false });
        store._addWorkflow(responseData.data);
        break;

      case SOCKET_EVENTS.WORKFLOW_UPDATED:
        useWorkflowStore.setState({ savingWorkflow: false });
        store._updateWorkflow(responseData.data);
        break;

      case SOCKET_EVENTS.WORKFLOW_CONFIG_UPDATED:
        useWorkflowStore.setState({ savingWorkflowConfig: false });
        store._updateWorkflow(responseData.data);
        break;

      case SOCKET_EVENTS.WORKFLOW_DELETED:
        useWorkflowStore.setState({ deletingWorkflow: false });
        store._removeWorkflow(responseData.data.workflow_id || responseData.data.id);
        break;

      // ✅ BOARD/ITEM SUCCESS EVENTS
      case SOCKET_EVENTS.WORKFLOW_BOARD_DATA:
        store._setBoardLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.CUSTOMER_ADDED_TO_WORKFLOW:
      case SOCKET_EVENTS.CUSTOMER_MOVED_TO_NODE:
      case SOCKET_EVENTS.CUSTOMER_REMOVED_FROM_WORKFLOW:
        // Reload board data to get fresh state
        if (responseData.data.workflow_id) {
          store.loadWorkflowBoard(responseData.data.workflow_id);
        }
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_WORKFLOWS_FAILED:
        useWorkflowStore.setState({ workflowsLoading: false });
        break;

      case SOCKET_EVENTS.GET_WORKFLOW_FAILED:
        useWorkflowStore.setState({ selectedWorkflowLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_WORKFLOW_FAILED:
        useWorkflowStore.setState({ creatingWorkflow: false });
        break;

      case SOCKET_EVENTS.UPDATE_WORKFLOW_FAILED:
        useWorkflowStore.setState({ savingWorkflow: false });
        break;

      case SOCKET_EVENTS.UPDATE_WORKFLOW_CONFIG_FAILED:
        useWorkflowStore.setState({ savingWorkflowConfig: false });
        break;

      case SOCKET_EVENTS.DELETE_WORKFLOW_FAILED:
        useWorkflowStore.setState({ deletingWorkflow: false });
        break;

      case SOCKET_EVENTS.GET_WORKFLOW_BOARD_FAILED:
        useWorkflowStore.setState({ boardLoading: false });
        break;

      // ✅ CLIENT AUTOMATION SUCCESS EVENTS
      case SOCKET_EVENTS.AUTOMATION_RUNS_LIST:
        store._setAutomationRunsLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.AUTOMATION_RUN_STARTED:
        store._addAutomationRun(responseData.data);
        break;

      case SOCKET_EVENTS.AUTOMATION_RUN_COMPLETED:
        store._updateAutomationRun(responseData.data);
        break;

      case SOCKET_EVENTS.AUTOMATION_SCHEDULE_UPDATED:
      case SOCKET_EVENTS.AUTOMATION_CONFIG_UPDATED:
        // Update the workflow in state with new automation_config
        if (responseData.data?.workflow) {
          store._updateWorkflow(responseData.data.workflow);
        }
        break;

      // ❌ CLIENT AUTOMATION ERROR EVENTS
      case SOCKET_EVENTS.GET_AUTOMATION_RUNS_FAILED:
        useWorkflowStore.setState({ automationRunsLoading: false });
        break;

      default:
        // Not a workflow-related event, ignore
        break;
    }
  };

  // Set up individual event listeners using existing system
  const workflowEvents = [
    // Workflow events
    SOCKET_EVENTS.WORKFLOWS_LIST,
    SOCKET_EVENTS.WORKFLOW_DETAILS,
    SOCKET_EVENTS.WORKFLOW_CREATED,
    SOCKET_EVENTS.WORKFLOW_UPDATED,
    SOCKET_EVENTS.WORKFLOW_CONFIG_UPDATED,
    SOCKET_EVENTS.WORKFLOW_DELETED,
    SOCKET_EVENTS.GET_WORKFLOWS_FAILED,
    SOCKET_EVENTS.GET_WORKFLOW_FAILED,
    SOCKET_EVENTS.CREATE_WORKFLOW_FAILED,
    SOCKET_EVENTS.UPDATE_WORKFLOW_FAILED,
    SOCKET_EVENTS.UPDATE_WORKFLOW_CONFIG_FAILED,
    SOCKET_EVENTS.DELETE_WORKFLOW_FAILED,
    // Board/Item events
    SOCKET_EVENTS.WORKFLOW_BOARD_DATA,
    SOCKET_EVENTS.CUSTOMER_ADDED_TO_WORKFLOW,
    SOCKET_EVENTS.CUSTOMER_MOVED_TO_NODE,
    SOCKET_EVENTS.CUSTOMER_REMOVED_FROM_WORKFLOW,
    // Client automation events
    SOCKET_EVENTS.AUTOMATION_RUNS_LIST,
    SOCKET_EVENTS.AUTOMATION_RUN_STARTED,
    SOCKET_EVENTS.AUTOMATION_RUN_COMPLETED,
    SOCKET_EVENTS.AUTOMATION_LOGS,
    SOCKET_EVENTS.AUTOMATION_CONFIG_UPDATED,
    SOCKET_EVENTS.AUTOMATION_SCHEDULE_UPDATED,
    SOCKET_EVENTS.GET_AUTOMATION_RUNS_FAILED,
    SOCKET_EVENTS.GET_AUTOMATION_LOGS_FAILED,
    SOCKET_EVENTS.TRIGGER_AUTOMATION_FAILED,
    SOCKET_EVENTS.UPDATE_AUTOMATION_CONFIG_FAILED,
    SOCKET_EVENTS.UPDATE_AUTOMATION_SCHEDULE_FAILED,
    SOCKET_EVENTS.GET_WORKFLOW_BOARD_FAILED,
    SOCKET_EVENTS.ADD_CUSTOMER_TO_WORKFLOW_FAILED,
    SOCKET_EVENTS.MOVE_CUSTOMER_TO_NODE_FAILED,
    SOCKET_EVENTS.REMOVE_CUSTOMER_FROM_WORKFLOW_FAILED
  ];

  // Set up individual event listeners using existing system
  workflowEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });

  // Set up auth reset listener
  universalHandler.on('auth_reset', () => {
    const store = useWorkflowStore.getState();
    store._handleAuthReset();
  });

  // Set up socket-disconnected listener to clear all saving flags
  window.addEventListener('socket-disconnected', () => {
    useWorkflowStore.getState()._handleDisconnect();
  });

  // Initialize the store
  useWorkflowStore.getState()._initialize(socketManager);

  console.log('✅ WorkflowStore: Socket event listeners registered');
};

// Legacy function - kept for backward compatibility
export const initializeWorkflowStore = (socketManager) => {
  initializeWorkflowStoreSocketListeners(socketManager);
};

export default useWorkflowStore;
